import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';
import { CardHistoryCoordinator, CardHistoryState, HistoryFaceIdentity } from './card-history-coordinator';
import { useUIStore } from '../../stores/ui';

type EditHistoryStore = {
  coordinator: CardHistoryCoordinator | null;
  editorSessionKey: string | null;
  historyState: CardHistoryState;
  ownerToken: symbol | null;
  resolveFaceIdentity: ((canvas: object) => HistoryFaceIdentity | null) | null;
};

const EMPTY_HISTORY_STATE: CardHistoryState = {
  canUndo: false,
  canRedo: false,
  isRestoring: false,
  lastRestoreError: null,
};

const EMPTY_STORE_STATE: EditHistoryStore = {
  coordinator: null,
  editorSessionKey: null,
  historyState: EMPTY_HISTORY_STATE,
  ownerToken: null,
  resolveFaceIdentity: null,
};

const editHistoryStore = createStore<EditHistoryStore>(() => EMPTY_STORE_STATE);
const coordinatorSubscriptions = new WeakMap<CardHistoryCoordinator, () => void>();

export const registerEditHistorySession = (
  coordinator: CardHistoryCoordinator,
  editorSessionKey: string,
  resolveFaceIdentity?: (canvas: object) => HistoryFaceIdentity | null,
): (() => void) => {
  const previousOwner = editHistoryStore.getState().ownerToken;
  if (previousOwner) useUIStore.getState().closeToaster(previousOwner);
  const previousCoordinator = editHistoryStore.getState().coordinator;
  if (previousCoordinator) {
    coordinatorSubscriptions.get(previousCoordinator)?.();
    coordinatorSubscriptions.delete(previousCoordinator);
  }

  const ownerToken = Symbol();
  const unsubscribe = coordinator.subscribe(() => {
    editHistoryStore.setState({ historyState: coordinator.getState() });
  });
  coordinatorSubscriptions.set(coordinator, unsubscribe);
  editHistoryStore.setState({
    coordinator,
    editorSessionKey,
    historyState: coordinator.getState(),
    ownerToken,
    resolveFaceIdentity: resolveFaceIdentity ?? null,
  });

  return () => {
    if (editHistoryStore.getState().ownerToken !== ownerToken) return;
    useUIStore.getState().closeToaster(ownerToken);
    coordinatorSubscriptions.get(coordinator)?.();
    coordinatorSubscriptions.delete(coordinator);
    editHistoryStore.setState(EMPTY_STORE_STATE);
  };
};

export const useEditHistoryCoordinator = (): CardHistoryCoordinator | null =>
  useStore(editHistoryStore, (state) => state.coordinator);

export const useEditHistorySessionKey = (): string | null =>
  useStore(editHistoryStore, (state) => state.editorSessionKey);

export const useEditHistorySessionOwner = (): symbol | null => useStore(editHistoryStore, (state) => state.ownerToken);

export const isCurrentEditHistorySession = (owner: symbol | null): boolean =>
  editHistoryStore.getState().ownerToken === owner;

export const useResolveEditHistoryFaceIdentity = (): ((canvas: object) => HistoryFaceIdentity | null) | null =>
  useStore(editHistoryStore, (state) => state.resolveFaceIdentity);

export const useCanUndo = (): boolean => useStore(editHistoryStore, (state) => state.historyState.canUndo);

export const useCanRedo = (): boolean => useStore(editHistoryStore, (state) => state.historyState.canRedo);

export const useIsRestoring = (): boolean => useStore(editHistoryStore, (state) => state.historyState.isRestoring);

export const useLastRestoreError = (): Error | null =>
  useStore(editHistoryStore, (state) => state.historyState.lastRestoreError);
